import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaUserCircle, FaSignOutAlt, FaUserEdit, FaCog, FaPlusCircle, FaTools, FaTimes, FaHeadset, FaStar, FaIdCard } from "react-icons/fa";
import styles from "../css/UserSidebar.module.css";

export default function UserSidebar({ user, onLogout, onClose }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [averageRating, setAverageRating] = React.useState(null);
  const spId = user?.spId || user?.userId;

  React.useEffect(() => {
    const storedLang = localStorage.getItem("lang");
    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
    }
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (!spId) {
      setAverageRating(null);
      return;
    }

    const cached = localStorage.getItem(`providerAverageRating:${spId}`);
    const cachedValue = Number(cached);
    setAverageRating(Number.isFinite(cachedValue) ? cachedValue : null);
  }, [spId]);
 
  return (
    <aside className={styles.sidebar}>
      {/* Close Button */}
      <button className={styles.closeButton} onClick={onClose} aria-label="Close sidebar">
        <FaTimes size={24} />
      </button>

      {/* Profile Section */}
      <div className={styles.profileSection}>
        <div className={styles.profileContent}>
          <div className={styles.avatarContainer}>
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt="Profile"
              />
            ) : (
              <FaUserCircle size={52} color="#cbd5e1" />
            )}
          </div>
          <div className={styles.userName}>
            {user?.name || "User"}
          </div>
          {user?.email && (
            <div className={styles.userEmail}>
              {user.email}
            </div>
          )}
          {user?.phone && (
            <div className={styles.userPhone}>
              +91 {user.phone}
            </div>
          )}
          <div className={styles.userId}>
            ID: {user?.spId || user?.userId || "N/A"}
          </div>
          <div className={styles.ratingBadge}>
            {averageRating !== null ? (
              <>
                <div className={styles.ratingStars} aria-label={`${averageRating.toFixed(1)} out of 5`}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={star <= Math.round(averageRating) ? styles.ratingStarFilled : styles.ratingStarEmpty}
                    />
                  ))}
                </div>
                <span className={styles.ratingText}>{averageRating.toFixed(1)}/5</span>
              </>
            ) : (
              <span className={styles.ratingText}>{t("UserSidebar.no_rating", "No rating yet")}</span>
            )}
          </div>
        </div>
      </div>

      {/* Menu Section */}
      <nav className={styles.menuSection}>
        <ul className={styles.menuList}>
          <li className={styles.menuItem}>
            <button
              className={styles.menuButton}
              onClick={() => {
                navigate("/profile");
                onClose();
              }}
            >
              <div className={styles.menuIcon}>
                <FaUserEdit size={20} />
              </div>
              <span className={styles.menuLabel}>
                {t("Manage.manage_profile", "Manage Profile")}
              </span>
            </button>
          </li>
          <li className={styles.menuItem}>
            <button
              className={styles.menuButton}
              onClick={() => {
                navigate("/add-service");
                onClose();
              }}
            >
              <div className={styles.menuIcon}>
                <FaPlusCircle size={20} />
              </div>
              <span className={styles.menuLabel}>
                {t("AddService.add_service", "Add Service")}
              </span>
            </button>
          </li>
          <li className={styles.menuItem}>
            <button
              className={styles.menuButton}
              onClick={() => {
                navigate("/manage");
                onClose();
              }}
            >
              <div className={styles.menuIcon}>
                <FaTools size={20} />
              </div>
              <span className={styles.menuLabel}>
                {t("Manage.manage_services", "Manage Services")}
              </span>
            </button>
          </li>
          <li className={styles.menuItem}>
            <button
              className={styles.menuButton}
              onClick={() => {
                navigate("/membership");
                onClose();
              }}
            >
              <div className={styles.menuIcon}>
                <FaIdCard size={20} />
              </div>
              <span className={styles.menuLabel}>
                {t("Membership.membership", "Membership")}
              </span>
            </button>
          </li>
          <li className={styles.menuItem}>
            <button
              className={styles.menuButton}
              onClick={() => {
                navigate("/contact");
                onClose();
              }}
            >
              <div className={styles.menuIcon}>
                <FaHeadset size={20} />
              </div>
              <span className={styles.menuLabel}>
                {t("UserSidebar.customer_support", "Customer Support")}
              </span>
            </button>
          </li>
          <li className={styles.menuItem}>
            <button
              className={styles.menuButton}
              onClick={() => {
                navigate("/settings");
                onClose();
              }}
            >
              <div className={styles.menuIcon}>
                <FaCog size={20} />
              </div>
              <span className={styles.menuLabel}>
                {t("Settings.settings", "Settings")}
              </span>
            </button>
          </li>
          <li className={styles.menuItem}>
            <button
              className={styles.logoutButton}
              onClick={() => {
                onLogout();
                onClose();
              }}
            >
              <div className={styles.logoutIcon}>
                <FaSignOutAlt size={20} />
              </div>
              <span className={styles.menuLabel}>
                {t("UserSidebar.logout", "Logout")}
              </span>
            </button>
          </li>
        </ul>
      </nav>
    </aside>
  );
}